import { useEffect, useRef, useState } from "react";
import { Card, CardBody, Col, Label, ListGroup, ListGroupItem, Row } from "reactstrap";
import * as echarts from 'echarts';
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { getClientMMPIncomeExpenseDetailsByClientId } from "../../../../Services/MMPClientServices/IncomeAndExpenses";
import { useSelector } from "react-redux";
import { RootState } from "../../../../ReduxToolkit/Store";
const formatNumberWithCommas = (value: number) => {
    return value?.toLocaleString();
};

export default function IncomeAndExpense({ dashboardData }: any) {
    const incomeChartRef = useRef<HTMLDivElement | null>(null);
    const assetsLiabilitiesChartRef = useRef<HTMLDivElement | null>(null);
    const userData = useSelector((state: RootState) => state.auth);
    const [categoryTotals, setCategoryTotals] = useState<any>({});
    const [totalOfCategories, setTotalOfCategories] = useState<any>({});
    const [assetsTotal, setAssetsTotal] = useState<any>(0);

    const {
        data: clientMMPIncomeExpenseDetailsResponse,
        isLoading: isClientMMPIncomeExpenseDetailsLoading,
        isError: isClientMMPIncomeExpenseDetailsError,
        refetch
    } = useGetRequestQuery<any>(
        ['getClientMMPIncomeExpenseDetailsByClientId', userData?.nameidentifier],
        () => getClientMMPIncomeExpenseDetailsByClientId(userData?.nameidentifier as string),
        {
            enabled: !!userData?.nameidentifier,
        }
    );

    useEffect(() => {
        const allCategories = {
            "Renting a Property": 0,
            "Child Care & Support": 0,
            "Education": 0,
            "Groceries": 0,
            "House you live in": 0,
            "Insurance": 0,
            // "Investment Property": 0,
            "Medical Expenses": 0,
            // "Ongoing Rent": 0,
            "Personal Care": 0,
            "Recreational & Entertainment": 0,
            "Telephone & Internet": 0,
            "Transport": 0,
        };

        const totals: { [key: string]: number } = { ...allCategories };
        let totalSum = 0;

        clientMMPIncomeExpenseDetailsResponse?.data?.forEach((item: any) => {
            const parentCategory = item.parentCategory;
            const monthlyAmountValue = parseFloat(item.monthlyAmountValue.replace(/,/g, '') || '0');

            if (totals[parentCategory] !== undefined) {
                totals[parentCategory] += monthlyAmountValue; // Update the total for this category
            }

            totalSum += monthlyAmountValue; // Sum all the amounts for total
        });

        setCategoryTotals(totals);
        setTotalOfCategories(totalSum);
    }, [clientMMPIncomeExpenseDetailsResponse, isClientMMPIncomeExpenseDetailsLoading]);

    useEffect(() => {
        if (incomeChartRef.current) {
            const incomeAndExpenseCategories = Object.keys(categoryTotals).map((category) => ({
                name: category,
                value: categoryTotals[category],
            }));
            const incomeOptions = {
                title: {
                    text: "",
                    left: "center",
                    top: "top",
                },
                tooltip: {
                    trigger: "item",
                },

                series: [
                    {
                        name: "Income & Expense",
                        type: "pie",
                        radius: ["40%", "70%"],
                        data: incomeAndExpenseCategories,
                        label: {
                            show: false,
                            position: "inside",
                            formatter: "{b}: {c}",
                        },
                        itemStyle: {
                            borderWidth: 0,
                        },
                    },
                ],
            };
            const incomeChart = echarts.init(incomeChartRef.current);
            incomeChart.setOption(incomeOptions);
            return () => {
                incomeChart.dispose();
            };
        }
    }, [categoryTotals]);

    // Static data for Assets & Liabilities chart
    useEffect(() => {
        if (assetsLiabilitiesChartRef.current) {
            const assetsLiabilitiesCategories = [
                { name: 'Total Assets', value: dashboardData[6]?.amount, itemStyle: { color: "#27ae60" } },
                { name: 'Total Liabilities', value: dashboardData[7]?.amount, itemStyle: { color: "#e74c3c" } },
            ];
            const assetsLiabilitiesOptions = {
                title: {
                    text: "",
                    left: "center",
                    top: "top",
                },
                tooltip: {
                    trigger: "item",
                },
                series: [
                    {
                        name: "Assets & Liabilities",
                        type: "pie",
                        radius: ["40%", "70%"],
                        data: assetsLiabilitiesCategories,
                        label: {
                            show: false,
                            position: "inside",
                            formatter: "{b}: {c}",
                        },
                        itemStyle: {
                            borderWidth: 0,
                        },
                    },
                ],
            };
            const assetsLiabilitiesChart = echarts.init(assetsLiabilitiesChartRef.current);
            assetsLiabilitiesChart.setOption(assetsLiabilitiesOptions);
            return () => {
                assetsLiabilitiesChart.dispose();
            };
        }
    }, [dashboardData]);

    useEffect(() => {
        const total =
            (dashboardData[7]?.amount || 0) +
            (dashboardData[6]?.amount || 0) +
            (dashboardData[9]?.amount || 0);
        setAssetsTotal(total || 0);
    }, [dashboardData]);

    return (
        <>
            <Row>
                <Col md={6}>
                    <h3 className="my-3 heading-color">Income & Expenses</h3>
                    <Card className="shadow-sm">
                        <CardBody>
                            <div className="text-center">
                                <div ref={incomeChartRef} style={{ width: '100%', height: '400px' }}></div>
                                <div className="total-amount mt-2" style={{ position: 'absolute', top: '3%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', fontWeight: 700, color: '#4f46e5' }}>
                                    <b style={{ fontSize: '20px' }}>Monthly Expenses</b>
                                    <span style={{ display: 'block', fontSize: '20px' }}>${totalOfCategories.toLocaleString()}</span>
                                </div>
                            </div>
                            <ListGroup className="mt-3">
                                {Object.keys(categoryTotals).map((category, index) => (
                                    <ListGroupItem key={index}>
                                        <div className="d-flex justify-content-between">
                                            <Label>{category}</Label>
                                            <div>
                                                <Label className="badge badge-primary mx-2">
                                                    {totalOfCategories > 0
                                                        ? (((categoryTotals[category] ?? 0) / totalOfCategories) * 100).toFixed(2) + "%"
                                                        : "0%"}
                                                </Label>
                                                <Label>${(categoryTotals[category] ?? 0).toLocaleString()}</Label>
                                            </div>

                                        </div>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>

                <Col md={6}>
                    <h3 className="my-3 heading-color">Assets & Liabilities</h3>
                    <Card className="shadow-sm">
                        <CardBody>
                            {/* Assets & Liabilities chart container */}
                            <div ref={assetsLiabilitiesChartRef} style={{ width: '100%', height: '400px' }}></div>
                            <div className="total-amount mt-2" style={{ position: 'absolute', top: '5%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', fontWeight: 700, color: '#4f46e5' }}>
                                <b style={{ fontSize: '20px' }}>Net Worth Amount</b>
                                <span style={{ display: 'block', fontSize: '20px' }}>${formatNumberWithCommas(dashboardData[6]?.amount - dashboardData[7]?.amount)}</span>
                            </div>
                            <ListGroup className="mt-3">
                                <ListGroupItem>
                                    <div className="d-flex justify-content-between">
                                        <Label>Total Assets</Label>
                                        <div>
                                            <Label className="badge badge-primary mx-2">
                                                {(((dashboardData[6]?.amount) / assetsTotal * 100) || 0).toFixed(2)}%
                                            </Label>
                                            <Label>${formatNumberWithCommas(dashboardData[6]?.amount || 0)}</Label>
                                        </div>
                                    </div>
                                </ListGroupItem>
                                <ListGroupItem>
                                    <div className="d-flex justify-content-between">
                                        <Label>Total Liabilities</Label>
                                        <div>
                                            <Label className="badge badge-primary mx-2">
                                                {(((dashboardData[7]?.amount) / assetsTotal * 100) || 0).toFixed(2)}%
                                            </Label>
                                            <Label>${formatNumberWithCommas(dashboardData[7]?.amount || 0)}</Label>
                                        </div>
                                    </div>
                                </ListGroupItem>
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
}
